// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getFormConfig } from '../form-definitions';
import { usePdfGenerator } from '../hooks/usePdfGenerator';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AuditFormCard from '../components/audit/AuditFormCard';
import AuditFolderBrowser from '../components/audit/AuditFolderBrowser';
import NewAuditFormDialog from '../components/audit/NewAuditFormDialog';
import ImportAuditPdfDialog from '../components/audit/ImportAuditPdfDialog';
import NextMonthS26Dialog from '../components/audit/NextMonthS26Dialog';
import WhatsAppNotifyDialog from '../components/audit/WhatsAppNotifyDialog';
import { exportFormsToExcel, exportFormsToPdf } from '../lib/auditExport';
import {
  ClipboardList,
  Plus,
  Loader2,
  Download,
  Upload,
  FolderOpen,
  BookOpen,
  Wrench,
  FileSpreadsheet,
  FileUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLOSING_MONTHS = ['novembro', 'fevereiro', 'maio', 'agosto'];

const FORM_META = {
  s25b: { label: 'S-25b', name: 'S-25b Congregação', area: 'congregacao', icon: BookOpen },
  s45b: { label: 'S-45b', name: 'S-45b Manutenção', area: 'manutencao', icon: Wrench },
  s26: { label: 'S-26', name: 'S-26 Folha de Contas', area: 'manutencao', icon: FileSpreadsheet },
  s44: { label: 'S-44', name: 'S-44 Relatório Mensal', area: 'manutencao', icon: ClipboardList },
  s24: { label: 'S-24', name: 'S-24 Recibo', area: 'congregacao', icon: ClipboardList },
};
const isLastMonthOfTrimester = (monthName) => {
  const normalized = monthName.toLowerCase().replace(/_/g, ' ').trim();
  return CLOSING_MONTHS.some(m => normalized.includes(m));
};

export default function AuditFormPage() {
  const { isAdmin } = useAuth();
  const { flattenPdfWithValues, downloadPdf } = usePdfGenerator();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [activeFormType, setActiveFormType] = useState(() => searchParams.get('type') || 's25b');
  const area = FORM_META[activeFormType]?.area || 'congregacao';

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Folder navigation state
  const [years, setYears] = useState([]);
  const [trimesters, setTrimesters] = useState([]);
  const [months, setMonths] = useState([]);
  const [subfolders, setSubfolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedTrimester, setSelectedTrimester] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [folderStack, setFolderStack] = useState([]);

  // New form dialog
  const [showNewForm, setShowNewForm] = useState(false);
  const [showImportPdf, setShowImportPdf] = useState(false);
  const [nextMonthForm, setNextMonthForm] = useState(null);
  const [selectedYearForForm, setSelectedYearForForm] = useState('');
  const [selectedTrimesterForForm, setSelectedTrimesterForForm] = useState('');
  const [selectedMonthForForm, setSelectedMonthForForm] = useState('');

  // WhatsApp notification
  const [showWhatsAppNotify, setShowWhatsAppNotify] = useState(false);
  const [completedFormInfo, setCompletedFormInfo] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area, activeFormType]);

  const handleTabChange = (type) => {
    setActiveFormType(type);
    setSearchParams({ type });
    const newArea = FORM_META[type]?.area || 'congregacao';
    localStorage.setItem('selectedArea', newArea);
  };

  const fetchForms = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/audit-forms?category=${area}&form_type=${activeFormType}`,
      );
      setForms(data.forms || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchYears = async () => {
    try {
      const { data } = await axios.get(`${API}/folders?category=${area}`);
      const folders = data.folders || data || [];
      setYears(folders.filter(f => !f.parent_id));
    } catch (err) { console.error(err); }
  };

  const handleSelectYear = async (year) => {
    setSelectedYear(year);
    setSelectedTrimester(null);
    setSelectedMonth(null);
    setMonths([]);
    setFiles([]);
    setLoadingFolders(true);
    try {
      const { data } = await axios.get(
        `${API}/folders?category=${area}&parent_id=${year.id}`,
      );
      setTrimesters(data.folders || data || []);
    } catch (err) { console.error(err); }
    finally { setLoadingFolders(false); }
  };

  const handleSelectTrimester = async (trimester) => {
    setSelectedTrimester(trimester);
    setSelectedMonth(null);
    setFiles([]);
    setLoadingFolders(true);
    try {
      const { data } = await axios.get(
        `${API}/folders?category=${area}&parent_id=${trimester.id}`,
      );
      setMonths(data.folders || data || []);
    } catch (err) { console.error(err); }
    finally { setLoadingFolders(false); }
  };

  const loadFolderContents = async (folderId) => {
    setLoadingFolders(true);
    try {
      const [foldersRes, filesRes] = await Promise.all([
        axios.get(`${API}/folders?category=${area}&parent_id=${folderId}`),
        axios.get(`${API}/files?category=${area}&folder_id=${folderId}`),
      ]);
      setSubfolders(foldersRes.data.folders || foldersRes.data || []);
      setFiles(filesRes.data.files || filesRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoadingFolders(false); }
  };

  const handleOpenSubfolder = async (folder) => {
    setFolderStack(prev => [...prev, folder]);
    await loadFolderContents(folder.id);
  };

  const handleBack = () => {
    if (folderStack.length > 0) {
      const newStack = folderStack.slice(0, -1);
      setFolderStack(newStack);
      const parentId = newStack.length > 0 ? newStack[newStack.length - 1].id : selectedMonth.id;
      loadFolderContents(parentId);
    } else if (selectedMonth) { setSelectedMonth(null); setFiles([]); setSubfolders([]); }
    else if (selectedTrimester) { setSelectedTrimester(null); setMonths([]); }
    else if (selectedYear) { setSelectedYear(null); setTrimesters([]); }
  };

  const handleSelectMonth = async (month) => {
    setSelectedMonth(month);
    setFolderStack([]);
    await loadFolderContents(month.id);

    if (selectedTrimester && isLastMonthOfTrimester(month.name)) {
      const yearName = selectedYear ? selectedYear.name.replace(/_/g, ' ') : '';
      const triName = selectedTrimester.name.replace(/_/g, ' ');
      const areaLabel = area === 'congregacao' ? 'Congregação' : 'Manutenção';
      setCompletedFormInfo({
        title: `${triName} - ${yearName}`.trim(),
        formType: areaLabel,
        date: new Date().toLocaleDateString('pt-BR'),
        isCloseTrimester: true,
      });
      setShowWhatsAppNotify(true);
    }
  };

  const handleCloseMonth = () => {
    if (!selectedMonth || !selectedYear) return;
    const yearName = selectedYear.name.replace(/_/g, ' ');
    const triName = selectedTrimester ? selectedTrimester.name.replace(/_/g, ' ') : '';
    const monthName = selectedMonth.name.replace(/_/g, ' ');
    const areaLabel = area === 'congregacao' ? 'Congregação' : 'Manutenção';
    setCompletedFormInfo({
      title: `${triName} - ${yearName}`.trim(),
      monthName,
      formType: areaLabel,
      date: new Date().toLocaleDateString('pt-BR'),
      isCloseMonth: true,
    });
    setShowWhatsAppNotify(true);
  };

  const handleDownloadFile = async (file) => {
    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao baixar'); }
  };

  const handleOpenFile = async (file) => {
    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank');
    } catch { toast.error('Erro ao abrir'); }
  };

  const handleUploadTemplate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('form_type', activeFormType);
    try {
      await axios.post(`${API}/audit-forms/upload-template`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const formLabel = FORM_META[activeFormType]?.label || activeFormType;
      toast.success(`Template ${formLabel} salvo!`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar template'); }
  };

  const isMonthlyForm = activeFormType === 's26' || activeFormType === 's44';

  const handleCreateForm = async () => {
    if (!selectedYearForForm || !selectedTrimesterForForm) {
      toast.error('Selecione o ano e o trimestre');
      return;
    }
    if (isMonthlyForm && !selectedMonthForForm) {
      toast.error('Selecione o mês');
      return;
    }
    setCreating(true);
    let title = `${selectedYearForForm} - ${selectedTrimesterForForm}`;
    const payload = { category: area, form_type: activeFormType };
    if (isMonthlyForm) {
      title = `${title} - ${selectedMonthForForm}`;
      // Ano civil do mês: set–dez usam o 1º ano do ano de serviço; jan–ago o 2º.
      const ym = selectedYearForForm.match(/(\d{4})\D+(\d{4})/) || selectedYearForForm.match(/(\d{4})/);
      let calYear = '';
      if (ym) {
        const firstHalf = ['Setembro', 'Outubro', 'Novembro', 'Dezembro'].includes(selectedMonthForForm);
        calYear = ym[2] ? (firstHalf ? ym[1] : ym[2]) : ym[1];
      }
      payload.field_values = activeFormType === 's26'
        ? { '900_4_Text_C': selectedMonthForForm, '900_5_Text_C': calYear }
        : { '900_2_Text': calYear ? `${selectedMonthForForm}/${calYear}` : selectedMonthForForm };
    }
    payload.title = title;
    try {
      await axios.post(`${API}/audit-forms`, payload);
      const formLabel = FORM_META[activeFormType]?.label || activeFormType;
      toast.success(`Relatório ${formLabel} criado!`);
      setShowNewForm(false);
      setSelectedYearForForm('');
      setSelectedTrimesterForForm('');
      setSelectedMonthForForm('');
      fetchForms();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar'); }
    finally { setCreating(false); }
  };

  const handleDeleteForm = async (form) => {
    if (!window.confirm(`Excluir "${form.title}"?`)) return;
    try {
      await axios.delete(`${API}/audit-forms/${form.id}`);
      toast.success('Excluído');
      fetchForms();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleToggleStatus = async (form) => {
    const newStatus = form.status === 'concluido' ? 'em_andamento' : 'concluido';
    try {
      const { data } = await axios.put(`${API}/audit-forms/${form.id}/status`, { status: newStatus });
      fetchForms();
      if (newStatus === 'concluido') {
        if (data.forecast_synced) {
          toast.success(`Previsão de Gastos ${data.forecast_synced} atualizada com o total deste S-44`);
        }
        const currentFormType = form.form_type || 's25b';
        const formTypeLabel = FORM_META[currentFormType]?.name || currentFormType;
        setCompletedFormInfo({
          title: form.title,
          formType: formTypeLabel,
          date: new Date().toLocaleDateString('pt-BR'),
          isCloseTrimester: false,
        });
        setShowWhatsAppNotify(true);
        toast.success('Relatório marcado como concluído!');
      }
    } catch { toast.error('Erro'); }
  };

  const handleDownloadForm = async (form) => {
    try {
      const valuesRes = await axios
        .get(`${API}/audit-forms/${form.id}/field-values`)
        .catch(() => ({ data: { field_values: {} } }));
      const savedValues = valuesRes.data.field_values || {};
      const hasSavedValues = Object.keys(savedValues).length > 0;
      const currentFormType = form.form_type || valuesRes.data.form_type || 's25b';
      const formDownloadLabel = FORM_META[currentFormType]?.label || currentFormType;

      let finalBytes;
      if (hasSavedValues) {
        finalBytes = await flattenPdfWithValues(currentFormType, savedValues);
      } else {
        const pdfRes = await axios.get(`${API}/audit-forms/${form.id}/download`, {
          responseType: 'arraybuffer',
        });
        finalBytes = pdfRes.data;
      }
      downloadPdf(finalBytes, `${formDownloadLabel}_${form.title}.pdf`);
    } catch { toast.error('Erro ao baixar'); }
  };

  const openFormInNewTab = (form) => {
    window.open(`/audit-form/edit/${form.id}`, '_blank');
  };

  // Build breadcrumb
  const breadcrumb = [];
  if (selectedYear) breadcrumb.push(selectedYear.name);
  if (selectedTrimester) breadcrumb.push(selectedTrimester.name);
  if (selectedMonth) breadcrumb.push(selectedMonth.name);
  folderStack.forEach(f => breadcrumb.push(f.name));

  const formConfig = getFormConfig(activeFormType);
  const formLabel = formConfig.title;

  return (
    <Layout>
      <div
        className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6"
        data-testid="audit-form-page"
      >
        {/* Header */}
        <div className="space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between sm:gap-3">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-foreground">
              Formulários de Auditoria
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              Relatórios de Auditoria das Contas
            </p>
          </div>
          <div className="flex gap-1.5 sm:gap-2 flex-shrink-0 flex-wrap justify-end">
            {forms.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportFormsToExcel(forms, formLabel)}
                  title="Exportar Excel"
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 sm:mr-1 text-emerald-600" />
                  <span className="hidden sm:inline">Excel</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportFormsToPdf(forms, formLabel)}
                  title="Exportar PDF"
                  data-testid="export-pdf-btn"
                >
                  <Download className="w-4 h-4 sm:mr-1 text-red-600" />
                  <span className="hidden sm:inline">PDF</span>
                </Button>
              </>
            )}
            {isAdmin() && (
              <label className="cursor-pointer" data-testid="upload-template-btn">
                <input type="file" accept=".pdf" className="hidden" onChange={handleUploadTemplate} />
                <Button variant="outline" size="sm" asChild>
                  <span>
                    <Upload className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Template {formLabel}</span>
                  </span>
                </Button>
              </label>
            )}
            {['s25b', 's45b', 's26', 's24'].includes(activeFormType) && (
              <Button variant="outline" size="sm" onClick={() => setShowImportPdf(true)} data-testid="import-pdf-btn">
                <FileUp className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Importar PDF</span>
              </Button>
            )}
            <Button size="sm" onClick={() => setShowNewForm(true)} data-testid="new-form-btn">
              <Plus className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Novo Relatório</span>
              <span className="sm:hidden">Novo</span>
            </Button>
          </div>
        </div>

        {/* Form Type Tabs */}
        <div
          className="flex gap-1 sm:gap-2 border-b border-border pb-0 overflow-x-auto"
          data-testid="form-type-tabs"
        >
          {Object.entries(FORM_META).map(([type, meta]) => {
            const TabIcon = meta.icon;
            return (
              <button
                key={type}
                onClick={() => handleTabChange(type)}
                data-testid={`tab-${type}`}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
                  activeFormType === type
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <TabIcon className="w-4 h-4" />
                <span className="hidden sm:inline">{meta.name}</span>
                <span className="sm:hidden">{meta.label}</span>
              </button>
            );
          })}
        </div>

        {/* Relatórios */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Relatórios {formLabel}
          </h2>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : forms.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-10">
                <ClipboardList className="w-12 h-12 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">
                  Nenhum relatório {formLabel} criado ainda
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {activeFormType !== 's25b'
                    ? `Faça upload do template ${FORM_META[activeFormType]?.label} primeiro e depois crie um relatório.`
                    : 'Clique em "Novo Relatório" para começar.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {forms.map(form => (
                <AuditFormCard
                  key={form.id}
                  form={form}
                  formLabel={formLabel}
                  onOpen={openFormInNewTab}
                  onDownload={handleDownloadForm}
                  onToggleStatus={handleToggleStatus}
                  onDelete={handleDeleteForm}
                  onNextMonth={activeFormType === 's26' ? setNextMonthForm : undefined}
                />
              ))}
            </div>
          )}
        </div>

        {/* Navegação por pastas */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-primary" />
            Documentos por Período
          </h2>
          <AuditFolderBrowser
            loading={loadingFolders}
            breadcrumb={breadcrumb}
            selectedYear={selectedYear}
            selectedTrimester={selectedTrimester}
            selectedMonth={selectedMonth}
            years={years}
            trimesters={trimesters}
            months={months}
            subfolders={subfolders}
            files={files}
            onBack={handleBack}
            onSelectYear={handleSelectYear}
            onSelectTrimester={handleSelectTrimester}
            onSelectMonth={handleSelectMonth}
            onOpenSubfolder={handleOpenSubfolder}
            onOpenFile={handleOpenFile}
            onDownloadFile={handleDownloadFile}
            onCloseMonth={handleCloseMonth}
          />
        </div>
      </div>

      <NewAuditFormDialog
        open={showNewForm}
        onOpenChange={setShowNewForm}
        formLabel={formLabel}
        formConfig={formConfig}
        years={years}
        selectedYear={selectedYearForForm}
        setSelectedYear={setSelectedYearForForm}
        selectedTrimester={selectedTrimesterForForm}
        setSelectedTrimester={setSelectedTrimesterForForm}
        monthly={isMonthlyForm}
        selectedMonth={selectedMonthForForm}
        setSelectedMonth={setSelectedMonthForForm}
        creating={creating}
        onCreate={handleCreateForm}
      />

      <ImportAuditPdfDialog
        open={showImportPdf}
        onOpenChange={setShowImportPdf}
        formType={activeFormType}
        formLabel={formLabel}
        category={area}
        years={years}
        onImported={(form) => { fetchForms(); navigate(`/audit-form/edit/${form.id}`); }}
      />

      <NextMonthS26Dialog
        open={!!nextMonthForm}
        onOpenChange={(o) => { if (!o) setNextMonthForm(null); }}
        form={nextMonthForm || {}}
        onCreated={(newForm) => {
          fetchForms();
          openFormInNewTab(newForm);
        }}
      />

      <WhatsAppNotifyDialog
        open={showWhatsAppNotify}
        onOpenChange={setShowWhatsAppNotify}
        info={completedFormInfo}
        onClose={() => {
          setShowWhatsAppNotify(false);
          setCompletedFormInfo(null);
        }}
      />
    </Layout>
  );
}
